import React from "react";
import { Search, Plus, ChevronLeft } from "lucide-react";
import PipelinePropertyRow from "./PipelinePropertyRow";

/**
 * List view for appraisal properties in a pipeline column (Unassigned, Cold, Warm, Hot).
 */
const CategoryListPropertyView = ({
    title,
    searchTerm,
    setSearchTerm,
    onBack,
    onPropertyClick,
    onAddAppraisal,
    onEditProperty,
    onChangeTemperature,
    onAddToDatabase,
    onListed,
    onLost,
    theme,
    properties
}) => {
    return (
        <div className="min-h-screen bg-[#FDFDFD] p-4 lg:p-2 font-sans text-[#353B48] animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 gap-3">
                <div className="flex items-center gap-3">
                    <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors -ml-2">
                        <ChevronLeft className="w-6 h-6 text-[#353B48]" />
                    </button>
                    <div>
                        <h1 className="text-[22px] font-bold tracking-tight text-[#353B48]">{title}</h1>
                        <p className="text-[#6B7280] text-xs font-medium">Manage and track your appraisal listings</p>
                    </div>
                </div>
                {/* No handler, no button — the same rule the cards follow, so a
                    read-only list (the Lost column, or a colleague's board
                    opened from the agent switcher) does not offer to add to it. */}
                {onAddAppraisal && (
                    <button
                        onClick={onAddAppraisal}
                        className="bg-[#55C5D0] hover:bg-[#45b5c0] text-white px-5 py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-all shadow-md shadow-[#55C5D0]/20 text-sm"
                    >
                        <Plus className="w-4 h-4" />
                        Add Appraisal
                    </button>
                )}
            </div>
            <div className="bg-white border border-[#E6E7E9] flex items-center px-4 py-3 mb-4 rounded-xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)]">
                <div className="relative w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search properties..."
                        className="w-full pl-10 pr-4 py-2 bg-white border border-[#F3F4F6] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] transition-all text-sm font-bold text-[#000000]"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>
            {/* Compact list. The ribbon and the rows were both shrunk so around
                ten appraisals fit on screen at once instead of two - Simon
                Abbott, 3 Sep 2026, on hundreds of appraisals being unscrollable. */}
            <div className="border-[0.85px] border-[#E4E6E9] p-2 rounded-xl">
                <div className={`${theme.ribbonBg} px-4 py-3 rounded-lg text-white mb-2 uppercase flex items-baseline justify-between gap-3`}>
                    <div>
                        <h2 className="text-base font-bold leading-tight">{theme.categoryTitle}</h2>
                        <p className="text-[11px] font-medium opacity-90">Appraisal listings in this stage</p>
                    </div>
                    <span className="text-xs font-bold opacity-90 shrink-0">
                        {properties.length} {properties.length === 1 ? "appraisal" : "appraisals"}
                    </span>
                </div>
                <div className="shadow-sm overflow-x-auto">
                    <div className="flex flex-col gap-1.5 p-2 sm:p-3 bg-white overflow-y-auto max-h-[calc(100vh-300px)] min-h-[240px] custom-scrollbar">
                        {properties.length > 0 ? (
                            properties.map((property) => (
                                <PipelinePropertyRow
                                    key={property.id}
                                    property={property}
                                    theme={theme}
                                    onClick={onPropertyClick}
                                    onEdit={onEditProperty}
                                    onChangeTemperature={onChangeTemperature}
                                    onAddToDatabase={onAddToDatabase}
                                    onListed={onListed}
                                    onLost={onLost}
                                />
                            ))
                        ) : (
                            <div className="text-center py-20 text-[#9CA3AF] font-medium">No appraisal listings in this category</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoryListPropertyView;
